import { getJson, requestJson } from './apiClient';
import {
  normalizeList,
  pickFirst,
  toNumberOrNull,
  toText,
} from './proyectosInmobiliariosUtils';
import { formatDateTime } from './prospectosCrmService';

const BASE_URL = '/api/admin/copiloto';

export const CANALES_COPILOTO = ['MESSENGER', 'INSTAGRAM', 'WHATSAPP', 'OTRO'];
export const ROLES_CONVERSACION = ['PROSPECTO', 'ASESOR', 'SISTEMA'];
export const ETAPAS_COPILOTO = [
  'NUEVO',
  'CONTACTADO',
  'CALIFICANDO',
  'INTERESADO',
  'CITA_AGENDADA',
  'VISITA_REALIZADA',
  'LLAMADA_AGENDADA',
  'CREDITO_EN_PROCESO',
  'NEGOCIACION',
  'APARTADO',
  'VENTA_CERRADA',
  'NO_INTERESADO',
  'SEGUIMIENTO',
];
export const TEMPERATURAS_COPILOTO = ['FRIO', 'TIBIO', 'CALIENTE', 'URGENTE'];
export const FORMAS_PAGO_COPILOTO = ['CONTADO', 'INFONAVIT', 'BANCARIO', 'FOVISSSTE', 'MIXTO', 'SIN_DEFINIR'];
export const PROXIMAS_ACCIONES_COPILOTO = ['RESPONDER', 'LLAMADA', 'CITA', 'VISITA', 'SEGUIMIENTO', 'ESCALAR', 'SIN_ACCION'];
export const PRIORIDADES_COPILOTO = ['BAJA', 'MEDIA', 'ALTA', 'URGENTE'];

export const CANAL_LABELS = {
  MESSENGER: 'Messenger',
  INSTAGRAM: 'Instagram',
  WHATSAPP: 'WhatsApp',
  OTRO: 'Otro',
};

export const ROL_CONVERSACION_LABELS = {
  PROSPECTO: 'Prospecto',
  ASESOR: 'Asesor',
  SISTEMA: 'Sistema',
};

export const ETAPA_COPILOTO_LABELS = {
  NUEVO: 'Nuevo',
  CONTACTADO: 'Contactado',
  CALIFICANDO: 'Calificando',
  INTERESADO: 'Interesado',
  CITA_AGENDADA: 'Cita agendada',
  VISITA_REALIZADA: 'Visita realizada',
  LLAMADA_AGENDADA: 'Llamada agendada',
  CREDITO_EN_PROCESO: 'Credito en proceso',
  NEGOCIACION: 'Negociacion',
  APARTADO: 'Apartado',
  VENTA_CERRADA: 'Venta cerrada',
  NO_INTERESADO: 'No interesado',
  SEGUIMIENTO: 'Seguimiento',
};

export const TEMPERATURA_COPILOTO_LABELS = {
  FRIO: 'Frio',
  TIBIO: 'Tibio',
  CALIENTE: 'Caliente',
  URGENTE: 'Urgente',
};

export const FORMA_PAGO_COPILOTO_LABELS = {
  CONTADO: 'Contado',
  INFONAVIT: 'INFONAVIT',
  BANCARIO: 'Credito bancario',
  FOVISSSTE: 'FOVISSSTE',
  MIXTO: 'Mixto',
  SIN_DEFINIR: 'Sin definir',
};

export const PROXIMA_ACCION_LABELS = {
  RESPONDER: 'Responder',
  LLAMADA: 'Llamada',
  CITA: 'Cita',
  VISITA: 'Visita',
  SEGUIMIENTO: 'Seguimiento',
  ESCALAR: 'Escalar',
  SIN_ACCION: 'Sin accion',
};

export const PRIORIDAD_LABELS = {
  BAJA: 'Baja',
  MEDIA: 'Media',
  ALTA: 'Alta',
  URGENTE: 'Urgente',
};

export const RESPUESTA_TIPO_LABELS = {
  RESPUESTA_PRINCIPAL: 'Respuesta principal',
  CONFIRMACION: 'Confirmacion',
  SEGUIMIENTO_SI_NO_CONTESTA: 'Seguimiento si no contesta',
  CIERRE_SUAVE: 'Cierre suave',
};

const normalizeCode = (value, fallback = '') => toText(value, fallback).toUpperCase();

export const labelFromCatalog = (labels, value, fallback = 'Sin definir') => {
  const key = normalizeCode(value);
  return labels[key] || (key ? key.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()) : fallback);
};

export const parseConversacionPegada = (text) => {
  const lines = String(text || '')
    .replace(/<[^>]*>/g, ' ')
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  return lines.map((line) => {
    const match = line.match(/^([^:：]{1,40})[:：]\s*(.+)$/);
    const speaker = normalizeCode(match?.[1] || '');
    const texto = (match?.[2] || line).trim();
    let rol = 'PROSPECTO';

    if (['ASESOR', 'CN', 'VENDEDOR', 'AGENTE'].includes(speaker)) {
      rol = 'ASESOR';
    } else if (['SISTEMA', 'BOT'].includes(speaker)) {
      rol = 'SISTEMA';
    }

    return {
      rol,
      texto,
      fechaHora: '',
    };
  });
};

export const getConversacionStats = (mensajes = []) => ({
  mensajes: mensajes.length,
  caracteres: mensajes.reduce((acc, item) => acc + String(item.texto || '').length, 0),
  excedeMensajes: mensajes.length > 100,
  excedeCaracteresPorMensaje: mensajes.some((item) => String(item.texto || '').length > 2000),
});

export const adaptAnalisisCopiloto = (data = {}) => ({
  proyectoDetectado: toText(data.proyectoDetectado),
  confianzaProyecto: Number(data.confianzaProyecto || 0),
  etapaComercial: normalizeCode(data.etapaComercial, 'NUEVO'),
  temperatura: normalizeCode(data.temperatura, 'TIBIO'),
  formaPago: normalizeCode(data.formaPago, 'SIN_DEFINIR'),
  intencionPrincipal: toText(data.intencionPrincipal),
  objecionesDetectadas: normalizeList(data.objecionesDetectadas).map((item) => toText(item)).filter(Boolean),
  resumen: toText(data.resumen),
  proximaAccion: {
    tipo: normalizeCode(data.proximaAccion?.tipo, 'SIN_ACCION'),
    fechaSugerida: toText(data.proximaAccion?.fechaSugerida),
    prioridad: normalizeCode(data.proximaAccion?.prioridad, 'MEDIA'),
    motivo: toText(data.proximaAccion?.motivo),
  },
  respuestasSugeridas: normalizeList(data.respuestasSugeridas).map((item, index) => ({
    id: String(pickFirst(item.id, index)),
    tipo: normalizeCode(item.tipo, 'RESPUESTA_PRINCIPAL'),
    texto: toText(item.texto),
  })).filter((item) => item.texto),
  etiquetasSugeridas: normalizeList(data.etiquetasSugeridas).map((item) => toText(item)).filter(Boolean),
  alertas: normalizeList(data.alertas).map((item) => toText(item)).filter(Boolean),
});

export const adaptInteraccionCopiloto = (item = {}) => ({
  id: toText(pickFirst(item.id, item.interaccionId, item.copilotoInteraccionId)),
  fecha: formatDateTime(pickFirst(item.fecha, item.fechaCreacion, item.createdAt)),
  canal: normalizeCode(item.canal, 'OTRO'),
  etapaComercial: normalizeCode(item.etapaComercial),
  temperatura: normalizeCode(item.temperatura),
  formaPago: normalizeCode(item.formaPago),
  resumen: toText(item.resumen),
  proximaAccionTipo: normalizeCode(item.proximaAccionTipo),
  proximaAccionFecha: formatDateTime(item.proximaAccionFecha, ''),
  proximaAccionPrioridad: normalizeCode(item.proximaAccionPrioridad),
  respuestaEnviada: toText(item.respuestaEnviada),
  etiquetas: normalizeList(item.etiquetas).map((tag) => toText(tag)).filter(Boolean),
  responsable: toText(pickFirst(item.responsable, item.responsableNombre, item.usuarioNombre)),
});

export const adaptHistorialCopiloto = (data = {}) => ({
  items: normalizeList(pickFirst(data.items, data.data, data.interacciones, data)).map(adaptInteraccionCopiloto),
  pagina: Number(pickFirst(data.pagina, data.page, 1)) || 1,
  tamanoPagina: Number(pickFirst(data.tamanoPagina, data.pageSize, 20)) || 20,
  totalPaginas: Number(pickFirst(data.totalPaginas, data.totalPages, 1)) || 1,
  totalRegistros: Number(pickFirst(data.totalRegistros, data.total, normalizeList(data).length)) || 0,
});

const cleanPayload = (payload = {}) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined && value !== ''));

export const analizarConversacionCopiloto = async (payload, options = {}) => {
  const data = await requestJson(`${BASE_URL}/analizar-conversacion`, {
    method: 'POST',
    body: cleanPayload({
      prospectoId: toNumberOrNull(payload.prospectoId),
      canal: normalizeCode(payload.canal, 'OTRO'),
      nombreProspecto: toText(payload.nombreProspecto),
      campanaOrigen: toText(payload.campanaOrigen),
      proyectoId: toNumberOrNull(payload.proyectoId),
      desarrolloId: toNumberOrNull(payload.desarrolloId),
      conversacion: normalizeList(payload.conversacion).map((item) => ({
        rol: normalizeCode(item.rol, 'PROSPECTO'),
        texto: toText(item.texto).slice(0, 2000),
        fechaHora: toText(item.fechaHora) || null,
      })),
    }),
    ...options,
  });

  return adaptAnalisisCopiloto(data);
};

export const guardarInteraccionCopiloto = (payload, options = {}) =>
  requestJson(`${BASE_URL}/guardar-interaccion`, {
    method: 'POST',
    body: cleanPayload({
      prospectoId: toNumberOrNull(payload.prospectoId),
      canal: normalizeCode(payload.canal, 'OTRO'),
      campanaOrigen: toText(payload.campanaOrigen) || null,
      proyectoId: toNumberOrNull(payload.proyectoId),
      desarrolloId: toNumberOrNull(payload.desarrolloId),
      resumen: toText(payload.resumen),
      etapaComercial: normalizeCode(payload.etapaComercial),
      temperatura: normalizeCode(payload.temperatura),
      formaPago: normalizeCode(payload.formaPago),
      intencionPrincipal: toText(payload.intencionPrincipal) || null,
      objeciones: normalizeList(payload.objeciones).map((item) => toText(item)).filter(Boolean),
      etiquetas: normalizeList(payload.etiquetas).map((item) => toText(item)).filter(Boolean),
      proximaAccionTipo: normalizeCode(payload.proximaAccionTipo) || null,
      proximaAccionFecha: toText(payload.proximaAccionFecha) || null,
      proximaAccionPrioridad: normalizeCode(payload.proximaAccionPrioridad) || null,
      respuestaEnviada: toText(payload.respuestaEnviada) || null,
    }),
    ...options,
  });

export const listarInteraccionesCopiloto = async (prospectoId, { pagina = 1, tamanoPagina = 20, ...options } = {}) => {
  const data = await getJson(`${BASE_URL}/prospectos/${prospectoId}/interacciones`, {
    ...options,
    query: {
      pagina,
      tamanoPagina,
    },
  });

  return adaptHistorialCopiloto(data);
};
